import {getWeekInfo, getMondayFromGlobalWeek} from './week-calculator';

/**
 * Locale-aware date helpers — the single, standard way to format dates in the UI.
 *
 * Dates respect the user's browser/OS locale and fall back to en-NZ when the
 * locale is missing or invalid, so NZ users see d/MM/yyyy while the format still
 * degrades gracefully on misconfigured machines. The order of day/month is
 * decided by the locale (en-US → MM/DD, en-NZ/en-GB → DD/MM); to change it a
 * user changes their device language/region settings (surfaced on /profile).
 */

const FALLBACK_LOCALE = 'en-NZ';

// Chrome on some Windows machines reports the display-language name
// (e.g. "English (New Zealand)") instead of a valid BCP 47 tag like "en-NZ".
// Recent Chrome versions throw on those invalid tags, so normalise the known ones.
const localeMap: Record<string, string> = {
    'English (New Zealand)': 'en-NZ',
    'English (United States)': 'en-US',
    'English (United Kingdom)': 'en-GB',
    'English (Australia)': 'en-AU'
};

/**
 * Resolves the effective locale, normalising known invalid Windows display
 * names and defaulting to navigator.language. Falls back to FALLBACK_LOCALE
 * when the tag is syntactically invalid (supportedLocalesOf throws) OR when the
 * runtime has no locale data for it (supportedLocalesOf returns []). Using
 * supportedLocalesOf is necessary because Intl.DateTimeFormat silently falls
 * back to the system default for unsupported-but-valid tags.
 */
export const normalizeLocale = (locale?: string): string => {
    const effective = locale ?? navigator.language ?? FALLBACK_LOCALE;
    const mapped = localeMap[effective] ?? effective;
    try {
        const supported = Intl.DateTimeFormat.supportedLocalesOf(mapped, {
            localeMatcher: 'lookup'
        });
        return supported.length > 0 ? mapped : FALLBACK_LOCALE;
    } catch {
        return FALLBACK_LOCALE;
    }
};

/**
 * Safely formats a date value as a locale date string, handling null/invalid
 * input and invalid locale tags. Returns an empty string for missing values.
 *
 * @param value - A Date, ISO string, timestamp, or null/undefined
 * @param locale - Optional locale override (defaults to navigator.language)
 * @param options - Optional Intl.DateTimeFormatOptions
 */
export const formatDateSafe = (
    value: Date | string | number | null | undefined,
    locale?: string,
    options?: Intl.DateTimeFormatOptions
): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    try {
        return date.toLocaleDateString(normalizeLocale(locale), options);
    } catch {
        return date.toLocaleDateString(FALLBACK_LOCALE, options);
    }
};

/**
 * Builds a date-fns format pattern (e.g. "dd/MM/yyyy") for the user's locale.
 * Used by react-datepicker, which needs a token string rather than Intl options,
 * so its input renders in the same order as everything else in the app.
 * Falls back to en-NZ ordering if the locale is invalid.
 */
export const getLocaleDateFormat = (): string => {
    const buildFormat = (locale: string): string => {
        const testDate = new Date(2024, 0, 15); // Jan 15, 2024
        const parts = new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(testDate);

        return parts
            .map(part => {
                if (part.type === 'day') return 'dd';
                if (part.type === 'month') return 'MM';
                if (part.type === 'year') return 'yyyy';
                if (part.type === 'literal') return part.value;
                return '';
            })
            .join('');
    };

    try {
        return buildFormat(normalizeLocale());
    } catch {
        return buildFormat(FALLBACK_LOCALE);
    }
};

/**
 * Short date format token for grid/picker display. Alias of getLocaleDateFormat.
 */
export const getLocaleShortDateFormat = (): string => getLocaleDateFormat();

/**
 * Back-compat wrapper retained for existing call sites. Prefer formatDateSafe.
 */
export function toLocaleDateStringSafe(
    date: Date,
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions
): string {
    const locale = Array.isArray(locales) ? locales[0] : locales;
    return formatDateSafe(date, locale, options) || date.toLocaleDateString();
}

/**
 * Formats a date string or Date object as a 2-digit numeric date in the user's
 * locale (e.g. 06/07/2026). Returns '' for null/invalid input.
 */
export const formatDate = (date: string | Date | undefined | null): string => {
    return formatDateSafe(date, undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Formats a date as a short numeric date string in the user's locale.
 */
const formatShortDate = (date: Date): string => {
    return formatDateSafe(date, undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Formats a global week number as "W{weekOfYear} {year} - {startDate}" (e.g., "W5 2025 - 27/01/2025")
 *
 * @param globalWeekNumber - The global week number to format
 * @param includeEndDate - If true, shows start and end dates
 * @returns A formatted week string with date or fallback format if error occurs
 */
export const formatWeekDisplay = (
    globalWeekNumber: number,
    includeEndDate: boolean = false
): string => {
    try {
        const monday = getMondayFromGlobalWeek(globalWeekNumber);
        const info = getWeekInfo(monday);
        const startDate = formatShortDate(monday);

        if (includeEndDate) {
            const sunday = new Date(monday);
            sunday.setDate(sunday.getDate() + 6);
            const endDate = formatShortDate(sunday);
            return `W${info.weekOfYear} ${info.year} - ${startDate} to ${endDate}`;
        }

        return `W${info.weekOfYear} ${info.year} - ${startDate}`;
    } catch (error) {
        console.error('Error formatting week display:', error);
        return `Week ${globalWeekNumber}`;
    }
};
